import React from 'react';
import { SERIES_COLORS } from '../utils/auditHelpers';

const SidebarSeriesChecklist = ({
    equityChartData,
    selectedSeries,
    setSelectedSeries,
    liveGridCount,
    sidebarSort,
    setSidebarSort,
    sidebarFilterMode,
    setSidebarFilterMode,
    setHighlightLive,
    sortedSeriesList,
    checkIsLiveGrid,
    hoveredSeries,
    setHoveredSeries,
    pinnedSeries,
    setPinnedSeries,
    activeSeriesMetrics,
    pnlUnit
}) => {
    return (
        <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 w-full lg:w-80 flex flex-col min-h-0 h-full overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-800 pb-3 mb-3 shrink-0">
                <div className="flex items-center gap-2">
                    <h4 className="font-bold text-xs text-gray-400 uppercase tracking-wider">Series Filters</h4>
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-mono font-bold border border-emerald-500/20" title="Total Live Grid Series Active">
                        {liveGridCount} Live
                    </span>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            const selectAll = {};
                            equityChartData.seriesList.forEach(s => { selectAll[s] = true; });
                            setSelectedSeries(selectAll);
                        }}
                        className="text-[10px] text-blue-400 hover:text-blue-300 font-bold uppercase transition-colors cursor-pointer"
                    >
                        Select All
                    </button>
                    <span className="text-gray-700 text-[10px]">|</span>
                    <button
                        onClick={() => {
                            const clearAll = {};
                            equityChartData.seriesList.forEach(s => { clearAll[s] = false; });
                            setSelectedSeries(clearAll);
                        }}
                        className="text-[10px] text-gray-400 hover:text-gray-200 font-bold uppercase transition-colors cursor-pointer"
                    >
                        Clear All
                    </button>
                </div>
            </div>

            {/* Sidebar sorting toggle */}
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-800/40 text-[10px] text-gray-500 shrink-0">
                <span className="font-semibold uppercase tracking-wider text-[9px] text-gray-400">Sort Checklist:</span>
                <div className="flex bg-[#14161f] p-0.5 rounded border border-gray-800 gap-0.5">
                    <button
                        onClick={() => setSidebarSort('pnl')}
                        className={`px-2.5 py-0.5 rounded text-[9px] font-bold transition-all cursor-pointer ${
                            sidebarSort === 'pnl' ? 'bg-blue-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        By PnL
                    </button>
                    <button
                        onClick={() => setSidebarSort('alphabetical')}
                        className={`px-2.5 py-0.5 rounded text-[9px] font-bold transition-all cursor-pointer ${
                            sidebarSort === 'alphabetical' ? 'bg-blue-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        By Name
                    </button>
                </div>
            </div>

            {/* Sidebar tick preset dropdown */}
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-800/40 text-[10px] text-gray-500 shrink-0">
                <span className="font-semibold uppercase tracking-wider text-[9px] text-gray-400">Auto-Tick:</span>
                <select
                    value={sidebarFilterMode}
                    onChange={(e) => {
                        const val = e.target.value;
                        setSidebarFilterMode(val);
                        if (val === 'live') {
                            setHighlightLive(true);
                        }
                    }}
                    className="bg-[#14161f] border border-gray-700 rounded px-2 py-0.5 text-[9px] text-gray-300 outline-none focus:border-blue-500 cursor-pointer font-bold"
                >
                    <option value="all">Top 5 by PnL</option>
                    <option value="active_regimes">Active Regimes</option>
                    <option value="live">Live Grid Only ({liveGridCount})</option>
                </select>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-1 pr-1">
                {sortedSeriesList.map((seriesName, idx) => {
                    const isChecked = selectedSeries[seriesName] || false;
                    const isLive = checkIsLiveGrid(seriesName);
                    const isHovered = hoveredSeries === seriesName;
                    const isPinned = pinnedSeries === seriesName;
                    const originalIndex = equityChartData.seriesList.indexOf(seriesName);
                    const color = SERIES_COLORS[originalIndex % SERIES_COLORS.length];
                    const metrics = activeSeriesMetrics[seriesName] || { totalPnL: 0, maxDD: 0, tradeCount: 0 };

                    return (
                        <div
                            key={seriesName}
                            onClick={() => {
                                setSelectedSeries(prev => ({ ...prev, [seriesName]: !prev[seriesName] }));
                            }}
                            onMouseEnter={() => setHoveredSeries(seriesName)}
                            onMouseLeave={() => setHoveredSeries(null)}
                            className={`flex items-center justify-between p-2 rounded-lg border transition-all cursor-pointer ${
                                isPinned
                                    ? 'bg-amber-500/20 border-amber-500 shadow-lg ring-2 ring-amber-500/50'
                                    : isHovered
                                        ? 'bg-blue-600/20 border-blue-500 shadow-md ring-1 ring-blue-500/50'
                                        : isChecked 
                                            ? 'bg-[#1e2230] border-gray-700/80 hover:border-gray-600' 
                                            : 'bg-transparent border-transparent hover:bg-gray-800/10'
                            }`}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {}}
                                    className="rounded bg-[#14161f] border-gray-700 text-blue-600 focus:ring-0 focus:ring-offset-0 pointer-events-none"
                                />
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPinnedSeries(prev => prev === seriesName ? null : seriesName);
                                    }}
                                    className={`text-[10px] p-0.5 rounded transition-all shrink-0 cursor-pointer ${
                                        isPinned
                                            ? 'text-amber-400 bg-amber-500/30 font-bold scale-110'
                                            : 'text-gray-600 hover:text-gray-300'
                                    }`}
                                    title={isPinned ? 'Click to Unpin/Unlock' : 'Click to Pin/Lock Line View'}
                                >
                                    📌
                                </button>
                                <span className="text-[10px] font-mono font-semibold text-gray-500 min-w-[20px] text-right shrink-0">
                                    {idx + 1}.
                                </span>
                                <span 
                                    className="w-2.5 h-2.5 rounded-full shrink-0" 
                                    style={{ backgroundColor: color }}
                                />
                                <div className="flex flex-col min-w-0">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="text-xs font-semibold text-gray-200 truncate" title={seriesName}>
                                            {seriesName}
                                        </span>
                                        {isLive && (
                                            <span className="px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-400 text-[8px] font-extrabold uppercase border border-emerald-500/30 shrink-0" title="Active Live Grid Strategy">
                                                LIVE
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-[10px] text-gray-500">
                                        {metrics.tradeCount} trades • DD: {pnlUnit === 'inr' 
                                            ? '₹' + Math.round(metrics.maxDD).toLocaleString('en-IN')
                                            : metrics.maxDD.toFixed(2) + '%'}
                                    </span>
                                </div>
                            </div>
                            <span className={`text-xs font-mono font-bold shrink-0 ${metrics.totalPnL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {pnlUnit === 'inr'
                                    ? (metrics.totalPnL >= 0 ? '+' : '') + '₹' + Math.round(metrics.totalPnL).toLocaleString('en-IN')
                                    : (metrics.totalPnL >= 0 ? '+' : '') + metrics.totalPnL.toFixed(2) + '%'}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SidebarSeriesChecklist;
